import React from 'react';
import ReactDOM from 'react-dom';

const CLASS_ATTRIBUTE_NAME = 'data-react-class';
const PROPS_ATTRIBUTE_NAME = 'data-react-props';

const registeredComponents = {};
const mountedRoots = [];
let ReactDOMClient;

const render = (node, Component) => {
  const propsJson = node.getAttribute(PROPS_ATTRIBUTE_NAME);
  const props = propsJson && JSON.parse(propsJson);

  const reactElement = <Component {...props} />;

  if (ReactDOMClient) {
    const root = ReactDOMClient.createRoot(node);

    root.render(reactElement);
    mountedRoots.push(root);
  } else {
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.render(reactElement, node);
  }
};

const registerComponents = components => {
  const alreadyExisting = [];

  Object.keys(components).forEach(key => {
    if (registeredComponents[key]) alreadyExisting.push(key);
    else {
      const comp = components[key];

      registeredComponents[key] = comp;
    }
  });

  if (alreadyExisting.length > 0) {
    // eslint-disable-next-line no-console
    console.error(
      `react-components-rails: cannot register components. Following components are already registered: ${alreadyExisting.join(
        ', ',
      )}`,
    );
  }

  return true;
};

const loadReactDOMClient = () => {
  return new Promise(resolve => {
    if (ReactDOMClient) resolve();

    import('react-dom/client')
      .then(i => {
        // with some bundlers, it can be imported as `.default`, while not with some others
        ReactDOMClient = i.default || i;
        resolve();
      })
      .catch(() => {
        // if this fails, then we will fallback to the legacy API
        ReactDOMClient = false;
        resolve();
      });
  });
};

const unmountComponents = () => {
  if (ReactDOMClient) {
    while (mountedRoots.length > 0) {
      const root = mountedRoots.pop();

      root.unmount();
    }
  } else {
    const mounted = document.querySelectorAll(`[${CLASS_ATTRIBUTE_NAME}]`);

    for (let i = 0; i < mounted.length; i += 1) {
      // eslint-disable-next-line react/no-deprecated
      ReactDOM.unmountComponentAtNode(mounted[i]);
    }
  }
};

const mountComponents = () => {
  const toMount = document.querySelectorAll(`[${CLASS_ATTRIBUTE_NAME}]`);

  for (let i = 0; i < toMount.length; i += 1) {
    const node = toMount[i];
    const componentName = node.getAttribute(CLASS_ATTRIBUTE_NAME);

    if (!componentName) {
      // eslint-disable-next-line no-console
      console.error(
        `react-components-rails: no ${CLASS_ATTRIBUTE_NAME} attribute on element: ${node}`,
      );
    } else {
      const component = registeredComponents[componentName];

      if (component) {
        if (node.innerHTML.length === 0) render(node, component);
      } else {
        // eslint-disable-next-line no-console
        console.error(
          `react-components-rails: cannot render a component that has not been registered: ${componentName}`,
        );
      }
    }
  }
};

const ready = callback => {
  if (/complete|interactive|loaded/.test(document.readyState)) {
    // In case the document has finished parsing, document's readyState will
    // be one of "complete", "interactive" or (non-standard) "loaded".
    callback();
  } else {
    // The document is not ready yet, so wait for the DOMContentLoaded event
    document.addEventListener('DOMContentLoaded', callback);
  }
};

const setup = async (components = {}) => {
  registerComponents(components);

  return loadReactDOMClient().then(() => {
    ready(mountComponents);
  });
};

export { setup, mountComponents as mount, unmountComponents as unmount };
